import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Compass,
  Heart,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

import { AiMark } from "@/components/ui/ai-mark";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { CelebrateItem } from "./overview-model";

interface Props {
  items: CelebrateItem[];
  loading?: boolean;
  onAsk: (prompt: string) => void;
}

const KIND_ICON: Record<CelebrateItem["kicker"], LucideIcon> = {
  celebrate: Sparkles,
  pep: Heart,
  tip: Compass,
};

const primaryButtonClassName = cn(
  "inline-flex h-9 w-fit items-center gap-1.5 rounded-g-sm bg-g-primary px-3.5 text-[12.5px] font-bold text-g-on-primary shadow-g-button transition-colors duration-150",
  "hover:bg-g-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
);

const pagerButtonClassName = cn(
  "flex size-6 items-center justify-center rounded-g-pill bg-g-bg-card text-g-primary shadow-g-card transition-colors duration-150 hover:bg-g-primary-wash",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
);

/**
 * "Värt att fira": a three-item carousel of good news and one coaching tip,
 * as the card in the sketch's Idag panel. A lilac wash, the kind as an icon
 * tile (named for assistive tech, no label above the title), the title at
 * 19/800 and the dawn illustration grounding the card. Tokens only.
 */
export function CelebrateCard({ items, loading = false, onAsk }: Props) {
  const { t } = useTranslation("dashboard");
  const [index, setIndex] = useState(0);
  const current = items[Math.min(index, Math.max(0, items.length - 1))];

  if (loading && items.length === 0) {
    return (
      <div className="rounded-g-xl border border-g-line bg-g-bg-card p-4 shadow-g-card">
        <Skeleton className="h-3 w-24 rounded" />
        <Skeleton className="mt-3 h-4 w-3/4 rounded" />
        <Skeleton className="mt-2 h-3 w-full rounded" />
      </div>
    );
  }
  if (!current) return null;

  const go = (next: number) => setIndex((next + items.length) % items.length);
  const Icon = KIND_ICON[current.kicker];
  const kind = t(`overview.celebrate.kicker.${current.kicker}`);
  const ctaLabel =
    current.cta.label || t(`overview.celebrate.cta.${current.kicker}`);

  return (
    <div
      className="relative flex flex-col gap-3 overflow-hidden rounded-g-xl border border-g-primary-tint bg-g-primary-subtle p-[18px_18px_0] shadow-g-card"
      data-testid="celebrate-card"
      data-kind={current.kicker}
    >
      <div className="flex items-center gap-2">
        <span
          role="img"
          aria-label={kind}
          title={kind}
          className="flex size-9 items-center justify-center rounded-g-pill bg-g-bg-card text-g-primary shadow-g-card"
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
        {current.ai ? <AiMark /> : null}
        {items.length > 1 ? (
          <span className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label={t("overview.celebrate.prev")}
              className={pagerButtonClassName}
            >
              <ChevronLeft className="size-3.5" aria-hidden="true" />
            </button>
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`${i + 1}/${items.length}`}
                className="flex h-5 items-center"
              >
                <span
                  className={cn(
                    "block h-[6px] rounded-g-pill transition-all duration-200",
                    i === index
                      ? "w-[16px] bg-g-primary"
                      : "w-[6px] bg-g-primary-muted",
                  )}
                />
              </button>
            ))}
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label={t("overview.celebrate.next")}
              className={pagerButtonClassName}
            >
              <ChevronRight className="size-3.5" aria-hidden="true" />
            </button>
          </span>
        ) : null}
      </div>
      <p className="text-[19px] font-extrabold leading-[1.2] tracking-[-0.02em] text-g-ink-900 [text-wrap:pretty]">
        {current.title}
      </p>
      <p className="text-[13.5px] leading-[1.5] text-g-ink-600 [text-wrap:pretty]">
        {current.body}
      </p>
      <div className="relative z-10 pb-[18px]">
        {current.cta.kind === "link" ? (
          <Link to={current.cta.to} className={primaryButtonClassName}>
            {ctaLabel}
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={() =>
              current.cta.kind === "ask" && onAsk(current.cta.prompt)
            }
            className={primaryButtonClassName}
          >
            {ctaLabel}
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      <img
        src="/illustrations/dawn-mountain.webp"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="g-illustration pointer-events-none absolute inset-x-0 bottom-0 h-[120px] w-full object-cover object-[center_60%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_55%)]"
      />
    </div>
  );
}
